// Scenes are optional. An episode can hold shots directly; scenes are a grouping you may use. These pure
// helpers decide what counts as a "real" scene in the UI and how an episode's shots are laid out.
//
// - The default scene (`is_default`, a column the backend adds; missing counts as false) is never shown as a
//   scene. It is where shots created straight in an episode live.
// - A scene is shown only when it has a name: a slugline. Unnamed scenes are structure the user never asked for.
// - Shots of unnamed scenes appear under "No scene" once real scenes exist.
import { safeGet, safeSet } from "./auth.ts";
import type { Scene, Shot } from "./types.ts";
import type { ProjectData } from "./useProject.ts";

export type EpisodeView = "scenes" | "all";

export const isDefaultScene = (s: Scene): boolean => s.is_default === true;
export const isNamedScene = (s: Scene): boolean => !isDefaultScene(s) && !!s.slugline?.trim();

export function namedScenes(d: ProjectData, episodeId: string): Scene[] {
  return (d.scenes.get(episodeId) ?? []).filter(isNamedScene);
}

/** Every shot of an episode in cut order: scene position, then the order inside the scene. Each shot once. */
export function episodeShots(d: ProjectData, episodeId: string): Shot[] {
  const out: Shot[] = [];
  const seen = new Set<string>();
  for (const sc of d.scenes.get(episodeId) ?? []) {
    for (const s of d.shots.get(sc.id) ?? []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push(s);
    }
  }
  return out;
}

export interface SceneGroup {
  key: string;
  /** null for the "No scene" group */
  scene: Scene | null;
  shots: Shot[];
}

/**
 * The episode's shots grouped by named scene, then the loose ones under "No scene". An episode without named
 * scenes returns one group with `scene: null` and `key: "all"`: the caller shows it flat, without a header.
 */
export function sceneGroups(d: ProjectData, episodeId: string): SceneGroup[] {
  const scenes = d.scenes.get(episodeId) ?? [];
  const named = scenes.filter(isNamedScene);
  if (named.length === 0) return [{ key: "all", scene: null, shots: episodeShots(d, episodeId) }];
  const groups: SceneGroup[] = named.map((scene) => ({
    key: scene.id,
    scene,
    shots: d.shots.get(scene.id) ?? [],
  }));
  const placed = new Set(groups.flatMap((g) => g.shots.map((s) => s.id)));
  const loose: Shot[] = [];
  for (const sc of scenes) {
    if (isNamedScene(sc)) continue;
    for (const s of d.shots.get(sc.id) ?? []) {
      if (placed.has(s.id)) continue;
      placed.add(s.id);
      loose.push(s);
    }
  }
  if (loose.length) groups.push({ key: "none", scene: null, shots: loose });
  return groups;
}

/** By scene is the default only where scenes were named. */
export const defaultEpisodeView = (d: ProjectData, episodeId: string): EpisodeView =>
  namedScenes(d, episodeId).length > 0 ? "scenes" : "all";

// ─── remembered per episode ──────────────────────────────────────────────────

export const episodeViewKey = (episodeId: string) => `filmsquare.episodeView.${episodeId}`;

export function loadEpisodeView(episodeId: string, fallback: EpisodeView): EpisodeView {
  const v = safeGet(episodeViewKey(episodeId));
  return v === "scenes" || v === "all" ? v : fallback;
}

export const saveEpisodeView = (episodeId: string, v: EpisodeView): void =>
  safeSet(episodeViewKey(episodeId), v);

// ─── where a new shot goes ───────────────────────────────────────────────────

/**
 * The scene a shot created straight in an episode is placed in: the backend's default scene when it flags one,
 * else the first unnamed scene, else the first scene. Null when the episode has no scenes yet, so the caller
 * creates one with an empty slugline. The one place to change once the backend has its own default-scene helper.
 */
export function pickPlacementScene(scenes: readonly Scene[]): Scene | null {
  const sorted = [...scenes].sort((a, b) => a.position - b.position);
  return sorted.find(isDefaultScene) ?? sorted.find((s) => !isNamedScene(s)) ?? sorted[0] ?? null;
}

/** "E01-07": the episode code and the next free number; shot codes are unique in a project. */
export function nextShotCode(taken: Iterable<string>, prefix: string, count: number): string {
  const codes = new Set(taken);
  let n = count + 1;
  const make = (i: number) => `${prefix}-${String(i).padStart(2, "0")}`;
  while (codes.has(make(n))) n++;
  return make(n);
}

/** Next free scene code in an episode: S1, S2, … */
export function nextSceneCode(taken: Iterable<string>, count: number): string {
  const codes = new Set(taken);
  let n = count + 1;
  while (codes.has(`S${n}`)) n++;
  return `S${n}`;
}
